/**
 * Cloud productions on the wire (PLAN §9, D10, D11, D13): a production that
 * runs in Fargate, launched from the portal, as a node of the tenant's
 * automatic cloud site (`site.kind = 'cloud'`, §9.1).
 *
 *   - **One record per production.** Launching creates the production (the
 *     ordinary record, `productions.ts`, in a studio of the cloud site) and
 *     this record beside it, which says how it runs: the task size, whether it
 *     is redundant (two tasks in two AZs, §9.3 — off by default, D11), when it
 *     starts, which package it is pinned to, and its tasks.
 *   - **Status** — `pending` (created; a scheduled start waits for its launch
 *     time, T−10 min, §9.2.4), `starting` (tasks placed, the node not ready
 *     yet), `running` (the main node enrolled, opened its site link and said
 *     it is ready), `stopping` (asked to stop: the task drains — the final
 *     as-run, the outbox — and exits), `stopped`, `failed` (it could not be
 *     launched, never became ready, or kept dying: `statusReason` says which).
 *   - **Cost (§9.6).** `cost` is an estimate from the on-demand Fargate price
 *     of the task size (`CLOUD_TASK_SIZES`), per task, from each task's start
 *     to its stop (or now). What is billed is metered in DynamoDB per task run.
 *
 * Cloud only, like the sites table: a node does not launch cloud productions.
 * `.strict()` as the rest of the contract.
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { sealedBoxWireSchema } from './sites.js';
import { boundedIdWireSchema, isoTimestampWireSchema, recordedPackageHashWireSchema, showStyleIdWireSchema, siteIdWireSchema, studioIdWireSchema } from './primitives.js';

const userRefSchema = z.string().min(1).max(128);

// ─── sizes and pricing (§9.6) ───

export const CLOUD_TASK_SIZE_IDS = ['small', 'medium', 'large'] as const;
/** Closed: the size a request asks for, and what the cloud prices and launches. */
export const cloudTaskSizeWireSchema = z.enum(CLOUD_TASK_SIZE_IDS);
export type CloudTaskSizeWire = z.infer<typeof cloudTaskSizeWireSchema>;
/** Open: a size as a reader receives it (a record, a usage run, the size list) — the catalog grows. */
export const cloudTaskSizeOpenWireSchema = openEnum(CLOUD_TASK_SIZE_IDS);
export type CloudTaskSizeOpenWire = z.infer<typeof cloudTaskSizeOpenWireSchema>;

/**
 * Fargate on-demand, Linux/ARM64 (Graviton — the runtime image is multi-arch),
 * eu-north-1, from the AWS Price List API on 2026-09-26:
 * `EUN1-Fargate-ARM-vCPU-Hours:perCPU` $0.0356, `EUN1-Fargate-ARM-GB-Hours`
 * $0.00392. An estimate for the portal (§9.6), not the invoice.
 */
export const FARGATE_ARM_PRICE_USD = { perVcpuHour: 0.0356, perGbHour: 0.00392 } as const;

/** One task size: what the task definition asks Fargate for (`cpu` units, MiB) and what an hour of it costs. */
export const cloudTaskSizeInfoWireSchema = z
  .object({
    taskSize: cloudTaskSizeOpenWireSchema,
    vcpu: z.number().positive(),
    memoryGb: z.number().positive(),
    /** One task for one hour, USD (on-demand). */
    hourlyUsd: z.number().nonnegative(),
  })
  .strict();
export type CloudTaskSizeInfoWire = z.infer<typeof cloudTaskSizeInfoWireSchema>;

/** PLAN §9.6: "roughly 0.5–1 vCPU and 1–2 GB is likely enough"; `large` for headroom while we measure. */
const SIZE_SHAPES: Readonly<Record<CloudTaskSizeWire, { vcpu: number; memoryGb: number }>> = {
  small: { vcpu: 0.5, memoryGb: 1 },
  medium: { vcpu: 1, memoryGb: 2 },
  large: { vcpu: 2, memoryGb: 4 },
};

/** Rounded to a millionth of a dollar: exact for these prices, without floating-point dust. */
function usd(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

/** The hourly price of one task of `size`. */
export function taskHourlyUsd(size: CloudTaskSizeWire): number {
  const shape = SIZE_SHAPES[size];
  return usd(shape.vcpu * FARGATE_ARM_PRICE_USD.perVcpuHour + shape.memoryGb * FARGATE_ARM_PRICE_USD.perGbHour);
}

export const CLOUD_TASK_SIZES: readonly CloudTaskSizeInfoWire[] = CLOUD_TASK_SIZE_IDS.map((taskSize) => ({ taskSize, ...SIZE_SHAPES[taskSize], hourlyUsd: taskHourlyUsd(taskSize) }));

/** The running estimate of a production: one task, or two when redundant (§9.3), per hour. */
export function productionHourlyUsd(size: CloudTaskSizeWire, redundant: boolean): number {
  return usd(taskHourlyUsd(size) * (redundant ? 2 : 1));
}

// ─── the record ───

export const CLOUD_PRODUCTION_STATUSES = ['pending', 'starting', 'running', 'stopping', 'stopped', 'failed'] as const;
/** Closed: what a query filters on. */
export const cloudProductionStatusWireSchema = z.enum(CLOUD_PRODUCTION_STATUSES);
export type CloudProductionStatusWire = z.infer<typeof cloudProductionStatusWireSchema>;
/** Open (D37 §4.3): a cloud production's status as a reader of another version sees it. */
export const cloudProductionStatusOpenWireSchema = openEnum(CLOUD_PRODUCTION_STATUSES);
export type CloudProductionStatusOpenWire = z.infer<typeof cloudProductionStatusOpenWireSchema>;

/** `stopped` and `failed`: nothing runs and nothing will. An `unknown` status (a newer cloud's) is not known to be over. */
export function isTerminalCloudStatus(status: CloudProductionStatusOpenWire): boolean {
  return status === 'stopped' || status === 'failed';
}

/** A task's role: the main node, or the backup of a redundant production (§9.3). */
export const cloudTaskRoleWireSchema = z.enum(['main', 'backup']);
export type CloudTaskRoleWire = z.infer<typeof cloudTaskRoleWireSchema>;

/**
 * One task's lifecycle, simplified from ECS's `lastStatus`: `launching` (the
 * cloud is calling RunTask), `provisioning` / `pending` (Fargate places it and
 * pulls the image), `running`, `stopping`, `stopped`.
 */
/** Open: ECS's task lifecycle as the cloud reports it. */
export const cloudTaskStatusWireSchema = openEnum(['launching', 'provisioning', 'pending', 'running', 'stopping', 'stopped']);
export type CloudTaskStatusWire = z.infer<typeof cloudTaskStatusWireSchema>;

export const cloudProductionTaskWireSchema = z
  .object({
    role: cloudTaskRoleWireSchema,
    /** ECS's task id (the last segment of its ARN), or null before RunTask answered. */
    taskId: z.string().max(64).nullable(),
    status: cloudTaskStatusWireSchema,
    availabilityZone: z.string().max(32).nullable(),
    /** The task's private VPC address (its "LAN IP", the peer link between a pair, §9.3). */
    privateIp: z.string().max(64).nullable(),
    /** The node the task enrolled as, once it has. */
    nodeId: boundedIdWireSchema.nullable(),
    /** 1 for the first launch of this role; each relaunch after an unexpected stop adds one (§9.2.6). */
    launch: z.number().int().positive(),
    launchedAt: isoTimestampWireSchema,
    startedAt: isoTimestampWireSchema.nullable(),
    stoppedAt: isoTimestampWireSchema.nullable(),
    /** ECS's stop code and reason, or the cloud's, for a stopped task. */
    stopReason: z.string().max(1000).nullable(),
  })
  .strict();
export type CloudProductionTaskWire = z.infer<typeof cloudProductionTaskWireSchema>;

export const cloudProductionCostWireSchema = z
  .object({
    /** The production's price per hour while it runs (all its tasks). */
    hourlyUsd: z.number().nonnegative(),
    /** Task-hours so far: every task run, start to stop (or now). */
    taskHours: z.number().nonnegative(),
    /** `taskHours` at the task size's price. */
    accruedUsd: z.number().nonnegative(),
  })
  .strict();

export const cloudProductionWireSchema = z
  .object({
    productionId: ulidSchema,
    studioId: studioIdWireSchema,
    /** Always the cloud site (§9.1); carried so a client needs no second lookup. */
    siteId: siteIdWireSchema,
    playlistId: ulidSchema.nullable(),
    rundownId: ulidSchema.nullable(),
    rehearsal: z.boolean(),
    status: cloudProductionStatusOpenWireSchema,
    /** Why it is in its status, for `failed`, `stopping` and `stopped` (and a relaunch), else null. */
    statusReason: z.string().max(1000).nullable(),
    redundant: z.boolean(),
    taskSize: cloudTaskSizeOpenWireSchema,
    /** Runs the simulated devices as a sidecar in the task (§9.5, for demos and tests). */
    simulators: z.boolean(),
    /** When the show starts, or null for "now". */
    startAt: isoTimestampWireSchema.nullable(),
    /** When the task(s) are, or were, launched: `startAt` − 10 min (§9.2.4), or the request's time. */
    launchAt: isoTimestampWireSchema,
    /** The production package it is pinned to (§4.1, D15), resolved at launch. */
    packageHash: recordedPackageHashWireSchema.nullable(),
    tasks: z.array(cloudProductionTaskWireSchema),
    /** Relaunches after unexpected stops so far (§9.2.6). */
    relaunches: z.number().int().nonnegative(),
    maxDurationMinutes: z.number().int().positive(),
    /** The safety net (§9.2.5): stopped when this passes, whatever it is doing. */
    deadlineAt: isoTimestampWireSchema,
    readyAt: isoTimestampWireSchema.nullable(),
    stopRequestedAt: isoTimestampWireSchema.nullable(),
    stopRequestedBy: userRefSchema.nullable(),
    stoppedAt: isoTimestampWireSchema.nullable(),
    /** The static addresses its traffic leaves AWS from (D11): what a customer allowlists. */
    egressIps: z.array(z.string()),
    cost: cloudProductionCostWireSchema,
    createdAt: isoTimestampWireSchema,
    createdBy: userRefSchema,
  })
  .strict();
export type CloudProductionWire = z.infer<typeof cloudProductionWireSchema>;

// ─── requests and responses ───

/** The longest a cloud production may run (§1.1: "up to 7 days continuous"). */
export const MAX_CLOUD_PRODUCTION_MINUTES = 7 * 24 * 60;
/** The default safety net: 12 hours, set per production at launch. */
export const DEFAULT_CLOUD_PRODUCTION_MINUTES = 12 * 60;
/** How far ahead a start may be scheduled. */
export const MAX_SCHEDULE_AHEAD_DAYS = 30;
/** Scheduled starts launch this long before `startAt` (§9.2.4: "T−10 min by default"). */
export const PRELAUNCH_MINUTES = 10;

export const launchCloudProductionRequestWireSchema = z
  .object({
    /** Client-chosen ULID: a retried launch returns the production as it is. */
    productionId: ulidSchema.optional(),
    /** A studio of the tenant's cloud site (§9.1). */
    studioId: studioIdWireSchema,
    /** The show style to run (D32); absent = the studio's `defaultShowStyleId`. */
    showStyleId: showStyleIdWireSchema.optional(),
    /** Run a playlist… */
    playlistId: ulidSchema.nullable(),
    /** …or a single rundown. Exactly one of the two. */
    rundownId: ulidSchema.nullable(),
    /** `now`, or when the show starts (the task launches `PRELAUNCH_MINUTES` before it). */
    startAt: z.union([z.literal('now'), isoTimestampWireSchema]),
    /** Two tasks in two AZs, main and backup, the cloud as witness (§9.3). Off by default (D11). */
    redundant: z.boolean(),
    taskSize: cloudTaskSizeWireSchema,
    /** Created for rehearsal (as `createProduction`). Default false. */
    rehearsal: z.boolean().optional(),
    /** Run the three simulated devices as a sidecar in the task (§9.5). Default false. */
    simulators: z.boolean().optional(),
    /** The safety net: the production is stopped after this long (default 12 h, at most 7 days). */
    maxDurationMinutes: z.number().int().min(15).max(MAX_CLOUD_PRODUCTION_MINUTES).optional(),
  })
  .strict()
  .refine((body) => (body.playlistId === null) !== (body.rundownId === null), {
    message: 'set exactly one of playlistId and rundownId',
    path: ['playlistId'],
  });
export type LaunchCloudProductionRequestWire = z.infer<typeof launchCloudProductionRequestWireSchema>;

export const stopCloudProductionRequestWireSchema = z
  .object({
    /** Why, for the record and the as-run log; null for none. */
    reason: z.string().max(500).nullable(),
  })
  .strict();

export const cloudProductionPathWireSchema = z.object({ productionId: ulidSchema.describe('The production’s id (a ULID).') }).strict();

export const listCloudProductionsQueryWireSchema = z
  .object({
    status: cloudProductionStatusWireSchema.optional().describe('Only cloud productions in this status.'),
    active: z.enum(['true', 'false']).optional().describe('`true`: only those not stopped or failed.'),
  })
  .strict();

/** Where cloud productions run and what they cost: the portal's launch dialog shows it (D11, §9.6). */
export const cloudProductionNetworkWireSchema = z
  .object({
    region: z.string(),
    /** The NAT gateway's Elastic IPs: every task's internet traffic leaves from these (D11). */
    egressIps: z.array(z.string()),
    sizes: z.array(cloudTaskSizeInfoWireSchema),
  })
  .strict();
export type CloudProductionNetworkWire = z.infer<typeof cloudProductionNetworkWireSchema>;

export const cloudProductionResponseWireSchema = z.object({ cloudProduction: cloudProductionWireSchema }).strict();
export const listCloudProductionsResponseWireSchema = z
  .object({
    /** Newest first. */
    cloudProductions: z.array(cloudProductionWireSchema),
    network: cloudProductionNetworkWireSchema,
  })
  .strict();

// ─── metering (§9.6) ───

export const usageMonthWireSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'a month is YYYY-MM');

export const cloudUsageQueryWireSchema = z.object({ month: usageMonthWireSchema.optional().describe('The month (UTC), `YYYY-MM`; default the current one.') }).strict();

/** One task run, as metered: from its start to its stop. */
export const cloudUsageRunWireSchema = z
  .object({
    productionId: ulidSchema,
    role: cloudTaskRoleWireSchema,
    taskId: z.string(),
    taskSize: cloudTaskSizeOpenWireSchema,
    startedAt: isoTimestampWireSchema,
    stoppedAt: isoTimestampWireSchema,
    hours: z.number().nonnegative(),
    costUsd: z.number().nonnegative(),
  })
  .strict();

export const cloudUsageResponseWireSchema = z
  .object({
    month: usageMonthWireSchema,
    /** Production-hours: each production's first start to its stop (a redundant production counts once). */
    productionHours: z.number().nonnegative(),
    /** Task-hours: every task run (a redundant production counts twice). */
    taskHours: z.number().nonnegative(),
    costUsd: z.number().nonnegative(),
    runs: z.array(cloudUsageRunWireSchema),
  })
  .strict();
export type CloudUsageResponseWire = z.infer<typeof cloudUsageResponseWireSchema>;

// ─── the task's side (`auth: 'node'`) ───

/**
 * What a cloud production's task is to do (§9.2.3), asked by its supervisor
 * with its node signature: which production, as which role, whether to stop,
 * and — for a redundant pair — its peer and the pair's LAN secret, sealed to
 * the asking node (context `pair-secret:<productionId>:<nodeId>`).
 */
export const cloudAssignmentWireSchema = z
  .object({
    productionId: ulidSchema,
    role: cloudTaskRoleWireSchema,
    status: cloudProductionStatusOpenWireSchema,
    /** Drain and exit (§9.2.5): asked to stop, the production ended, or its deadline passed. */
    stop: z.boolean(),
    stopReason: z.string().max(1000).nullable(),
    /** The other task of a redundant pair, once it has enrolled; null otherwise. */
    peer: z.object({ nodeId: boundedIdWireSchema, lanIp: z.string() }).strict().nullable(),
    /** The pair's secret (`ACP_PEER_SECRET`), sealed to this node; null unless redundant. */
    pairSecret: sealedBoxWireSchema.nullable(),
  })
  .strict();
export type CloudAssignmentWire = z.infer<typeof cloudAssignmentWireSchema>;

/** The seal context of the pair secret for one node: a box cannot be replayed for another production or node. */
export function pairSecretContext(productionId: string, nodeId: string): string {
  return `pair-secret:${productionId}:${nodeId}`;
}

export const cloudAssignmentResponseWireSchema = z.object({ assignment: cloudAssignmentWireSchema }).strict();

export const cloudReadyRequestWireSchema = z.object({ productionId: ulidSchema }).strict();
